// Imported from the module rather than the package root: the root barrel pulls in
// the preview components, and through them `web-sdk-default-ui`'s Dialogue, whose
// `@mui/utils/useEventCallback` directory import Node cannot resolve as ESM — which
// takes this file's tests down with it. The exports map (`"./*"`) makes the subpath
// a supported entry, and this module only pulls `theme/tokens`.
import { COMPOSER_SIZE_RANGES } from "@chatbotgang/cl-webstream-ui/webStreamComposerSizeUtils";
import { BASE_FONT_SIZE_RANGE } from "@chatbotgang/cl-webstream-ui/webStreamTypographyCssVarUtils";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@/components/Accordion";
import { Select } from "@/components/Select";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { useWebStreamConfigForm } from "@/pages/channels/hooks/form/useWebStreamConfigForm";

type WebStreamConfig = InterludeTypes["WebStreamConfig"];
type BannerScale = NonNullable<WebStreamConfig["menu"]["bannerScale"]>;
type ComposerField = "composer.inputMinHeight" | "composer.buttonSize";

/**
 * A stored size outside the slider's range is not corrected, only displayed
 * corrected: Vivace's renderer clamps to the same bounds, so the clamped number
 * is what the channel actually renders, while the form keeps the stored value
 * so a save that never touched this field writes it back unchanged.
 */
const clampBaseFontSize = (value: number) =>
  Math.min(Math.max(value, BASE_FONT_SIZE_RANGE.min), BASE_FONT_SIZE_RANGE.max);

const styles = {
  section: {
    backgroundColor: theme.colors.white,
    borderRadius: "8px",
  },
  // Matches one cell of HeroAndColorForm's three-column color grid, so the
  // sections line up instead of these controls spanning the whole form.
  field: {
    width: "100%",
    maxWidth: "320px",
  },
  sliderRow: {
    alignItems: "center",
    gap: "16px",
  },
  // Minimum width so the row does not reflow as a numeric readout goes from
  // "14 px" to "22 px" while the slider is being dragged. It is a floor, not a
  // fixed width, because "Default" runs longer than 48px in some locales (ja
  // reads "デフォルト") and would otherwise wrap onto two lines. In those
  // locales the track still shifts once when a composer slider leaves or
  // returns to "Default".
  sliderValue: {
    minWidth: "48px",
    flexShrink: 0,
    whiteSpace: "nowrap",
    textAlign: "right",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

/**
 * One composer slider. Unlike the base font size, an untouched channel has no
 * stored value and the form fills none (see WebStreamComposerSchema): the
 * slider rests at the bottom of its range and reads "Default" until it is
 * moved, so opening and saving the page never changes how a channel renders.
 * Moving it back to the bottom clears the value again, which is how a channel
 * returns to default-ui's composer; at that end the two are the same size
 * (28px buttons exactly, the 5% input within a few px of default-ui's 40px).
 */
const ComposerSizeSlider: React.FC<{
  name: ComposerField;
  range: { min: number; max: number };
  unit: string;
  label: string;
}> = function ComposerSizeSlider({ name, range, unit, label }) {
  const { t } = useTranslation();
  const { control } = useFormContext<WebStreamConfig>();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        // `null` rather than `undefined` for a cleared slider: react-hook-form's
        // Controller reads an `undefined` value as "use the default value", so a
        // channel that had a stored size would snap back to it. The schema's
        // `.catch(undefined)` turns the null into "not set" when the form submits.
        const stored: number | null | undefined = field.value;
        const displayed =
          stored == null
            ? range.min
            : Math.min(Math.max(stored, range.min), range.max);
        return (
          <Stack direction="row" sx={{ ...styles.field, ...styles.sliderRow }}>
            <Slider
              value={displayed}
              onChange={(_, value) => {
                const picked = Array.isArray(value) ? value[0] : value;
                field.onChange(picked === range.min ? null : picked);
              }}
              min={range.min}
              max={range.max}
              step={1}
              valueLabelDisplay="auto"
              aria-label={label}
            />
            <Box sx={styles.sliderValue}>
              <Typography variant="body">
                {stored == null
                  ? t("webStreamConfig.composer.default")
                  : `${displayed} ${unit}`}
              </Typography>
            </Box>
          </Stack>
        );
      }}
    />
  );
};

export const SizeForm: React.FC = function SizeForm() {
  const { t } = useTranslation();
  const { control } = useFormContext<WebStreamConfig>();

  // Built with `t()` rather than the module-level `<Trans>` element lists the
  // sibling forms use: `Select` forwards its options to MUI's Autocomplete,
  // whose `getOptionLabel` is not overridable here and reads `option.label` as
  // the input's display string — a React element there renders as `[object
  // Object]`. The keys stay literal, so i18n-ally still resolves them.
  const bannerScaleOptions = useMemo(
    () =>
      [
        {
          value: "small",
          label: t("webStreamConfig.banner.scale.small"),
        },
        {
          value: "medium",
          label: t("webStreamConfig.banner.scale.medium"),
        },
        {
          value: "large",
          label: t("webStreamConfig.banner.scale.large"),
        },
      ] satisfies Array<{ value: BannerScale; label: string }>,
    [t],
  );

  return (
    <Accordion defaultExpanded>
      <AccordionSummary>
        <Typography variant="h3" fontWeight={500}>
          {t("webStreamConfig.size.title")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack sx={styles.section} gap="16px">
          <Stack gap="8px">
            <Typography variant="h3" fontWeight={500}>
              {t("webStreamConfig.typography.baseFontSize.label")}
            </Typography>
            <Typography variant="note">
              {t("webStreamConfig.typography.baseFontSize.hint")}
            </Typography>
            <Controller
              name="typography.baseFontSize"
              control={control}
              render={({ field }) => {
                const displayed = clampBaseFontSize(
                  field.value ??
                    useWebStreamConfigForm.defaultWebStreamConfig.typography
                      .baseFontSize,
                );
                return (
                  <Stack
                    direction="row"
                    sx={{ ...styles.field, ...styles.sliderRow }}
                  >
                    <Slider
                      value={displayed}
                      onChange={(_, value) => {
                        field.onChange(Array.isArray(value) ? value[0] : value);
                      }}
                      min={BASE_FONT_SIZE_RANGE.min}
                      max={BASE_FONT_SIZE_RANGE.max}
                      step={1}
                      marks
                      valueLabelDisplay="auto"
                      aria-label={t(
                        "webStreamConfig.typography.baseFontSize.label",
                      )}
                    />
                    {/* `sx` goes on a Box: this Typography is emotion-based
                        and takes no sx, same as PageTitle does it. */}
                    <Box sx={styles.sliderValue}>
                      <Typography variant="body">{`${displayed} px`}</Typography>
                    </Box>
                  </Stack>
                );
              }}
            />
          </Stack>
          <Stack gap="8px">
            <Typography variant="h3" fontWeight={500}>
              {t("webStreamConfig.banner.title")}
            </Typography>
            <Typography variant="note">
              {t("webStreamConfig.banner.scale.hint")}
            </Typography>
            <Controller
              name="menu.bannerScale"
              control={control}
              render={({ field }) => (
                <Stack sx={styles.field}>
                  <Select<BannerScale>
                    options={bannerScaleOptions}
                    // A stored config can omit `bannerScale`; the form model
                    // fills it, but this keeps the control on the documented
                    // "absent means medium" default rather than showing empty.
                    value={
                      field.value ??
                      useWebStreamConfigForm.defaultWebStreamConfig.menu
                        .bannerScale
                    }
                    onChange={field.onChange}
                    disableClearable
                  />
                </Stack>
              )}
            />
          </Stack>
          <Stack gap="8px">
            <Typography variant="h3" fontWeight={500}>
              {t("webStreamConfig.composer.inputMinHeight.label")}
            </Typography>
            <Typography variant="note">
              {t("webStreamConfig.composer.inputMinHeight.hint")}
            </Typography>
            <ComposerSizeSlider
              name="composer.inputMinHeight"
              range={COMPOSER_SIZE_RANGES.inputMinHeight}
              unit="%"
              label={t("webStreamConfig.composer.inputMinHeight.label")}
            />
          </Stack>
          <Stack gap="8px">
            <Typography variant="h3" fontWeight={500}>
              {t("webStreamConfig.composer.buttonSize.label")}
            </Typography>
            <Typography variant="note">
              {t("webStreamConfig.composer.buttonSize.hint")}
            </Typography>
            <ComposerSizeSlider
              name="composer.buttonSize"
              range={COMPOSER_SIZE_RANGES.buttonSize}
              unit="px"
              label={t("webStreamConfig.composer.buttonSize.label")}
            />
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
